import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Waves, ThermometerSun, Star } from 'lucide-react';
import type { Loch } from '@/app/lib/types';
import { PlaceHolderImages } from '@/lib/placeholder-images';

type LocationCardProps = {
  location: Loch;
};

export function LocationCard({ location }: LocationCardProps) {
  const image = PlaceHolderImages.find((img) => img.id === location.imageId);

  return (
    <Card className="group overflow-hidden transition-smooth hover:shadow-loch-lg hover:-translate-y-2 animate-fade-in">
      <Link to={`/locations/${location.slug}`} className="block">
        <CardHeader className="p-0">
          {/* Hero Image with Gradient Overlay */}
          <div className="relative h-44 sm:h-52 md:h-56 w-full overflow-hidden">
            {image ? (
              <>
                <img
                  src={image.imageUrl}
                  alt={image.description}
                  className="object-cover w-full h-full group-hover:scale-110 transition-smooth"
                  data-ai-hint={image.imageHint}
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20">
                <MapPin className="h-10 sm:h-12 md:h-14 w-10 sm:w-12 md:w-14 text-primary/40" />
              </div>
            )}

            {/* Floating Region Badge */}
            <div className="absolute top-3 left-3">
              <Badge className="bg-white/90 text-foreground hover:bg-white backdrop-blur-sm shadow-loch border-0 font-semibold">
                <MapPin className="h-3 w-3 mr-1" />
                {location.region}
              </Badge>
            </div>

            {/* Title Overlay at Bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="text-lg sm:text-xl font-bold text-white drop-shadow-lg line-clamp-2">
                {location.name}
              </h3>
            </div>
          </div>
        </CardHeader>

        {/* Content Section */}
        <CardContent className="p-4 space-y-3">
          {/* Location Features */}
          <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-muted-foreground">
            {location.waterQuality && (
              <div className="flex items-center gap-1">
                <Waves className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                <span className="font-medium">{location.waterQuality}</span>
              </div>
            )}
            {location.avgTemp && (
              <div className="flex items-center gap-1">
                <ThermometerSun className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent" />
                <span className="font-medium">{location.avgTemp}°C</span>
              </div>
            )}
            {location.difficulty && (
              <div className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-secondary" />
                <span className="font-medium">{location.difficulty}</span>
              </div>
            )}
          </div>

          {/* Description Preview */}
          {location.description && (
            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 leading-relaxed">
              {location.description}
            </p>
          )}

          {/* View Details Prompt */}
          <div className="pt-2 flex items-center text-primary text-sm font-semibold group-hover:gap-2 transition-all">
            <span>View Details</span>
            <span className="inline-block opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all">→</span>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
